"use client";

import EmailSignup from "@/components/EmailSignup";
import Script from "next/script";
import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import PremiumNavbar from "@/components/PremiumNavbar";

export default function Home() {
  const [showModal, setShowModal] = useState(false);

  // Calculate days until next Friday
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 is Sunday, 6 is Saturday
  const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 7 + 5 - dayOfWeek;

  // Create countdown text based on days remaining
  let countdownText = "";
  if (daysUntilFriday === 0) {
    countdownText = "Next issue drops today!";
  } else if (daysUntilFriday === 1) {
    countdownText = "Next issue drops tomorrow!";
  } else {
    countdownText = `Next issue drops in ${daysUntilFriday} days`;
  }

  // Sample latest issues data - in a real app, you'd fetch this dynamically
  const latestIssues = [
    {
      id: "016",
      date: "August 15, 2025",
      title: "Alignment Over Acceleration",
      link: "/previous-issues/issue-016",
    },
    {
      id: "015",
      date: "August 8, 2025",
      title: "The Strategic Power of Stillness",
      link: "/previous-issues/issue-015",
    },
  ];

  return (
    <>
      <Script
        id="newsletter-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            url: "https://webfindsfriday.com/",
            name: "WebFindsFriday",
            description: "Curated Gems Delivered Weekly",
            potentialAction: {
              "@type": "SearchAction",
              target:
                "https://webfindsfriday.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />

      <PremiumNavbar onSubscribe={() => setShowModal(true)} />

      {/* Subscribe Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-white/40 backdrop-blur-md"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full relative animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close subscribe modal"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-4 text-center">
              Subscribe to WebFindsFriday
            </h2>
            <EmailSignup autoFocus={showModal} />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-32 md:pb-24 px-4 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-black text-white mb-6">
                <span className="animate-pulse mr-2 h-2 w-2 rounded-full bg-green-400"></span>
                {countdownText}
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
                Curated Internet{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-indigo-500 bg-clip-text text-transparent relative z-10">
                    Gems
                  </span>
                  <span className="absolute bottom-1 left-0 w-full h-3 bg-yellow-300 opacity-30 z-0 pointer-events-none"></span>
                </span>
                <br className="hidden md:block" />
                Delivered{" "}
                <span className="relative inline-block">
                  <span className="z-10 relative">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500">
                      Weekly
                    </span>
                  </span>
                  <span className="absolute left-0 right-0 -bottom-1 h-1 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 opacity-70"></span>
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 max-w-lg mb-8">
                Discover sharper ideas, hidden tools, and rare opportunities in
                under 5 minutes a week. No fluff, only value.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 mb-12"
                id="signup"
              >
                <div className="flex-1">
                  <EmailSignup />
                </div>
              </div>

              {/* Social Proof */}
              <div className="flex items-center">
                <div className="flex -space-x-3 mr-4">
                  {[1, 2, 3].map((num) => (
                    <img
                      key={num}
                      className="h-8 w-8 rounded-full ring-2 ring-white"
                      src={`/avatar${num}.${num === 2 ? "webp" : "jpg"}`}
                      alt={`Member ${num}`}
                    />
                  ))}
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-800 text-xs font-medium text-white ring-2 ring-white">
                    +99
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">2,100+</span>{" "}
                  curious minds
                </p>
              </div>
            </div>

            {/* Floating Newsletter Preview */}
            <div className="relative hidden md:block">
              <div className="absolute -top-6 -left-6 w-64 h-64 bg-gray-100 rounded-full opacity-30"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-yellow-100 rounded-full opacity-40"></div>

              <div className="relative bg-white p-8 border border-gray-200 rounded-xl shadow-xl rotate-1 z-10">
                <div className="flex justify-between items-center mb-6">
                  <Image
                    src="/bannerLogo.png"
                    alt="WebFindsFriday Newsletter"
                    width={120}
                    height={30}
                    className="h-6 w-auto"
                  />
                  <span className="text-xs text-gray-500">Preview</span>
                </div>

                <h3 className="text-lg font-semibold mb-4">
                  This Week&apos;s Internet Gems
                </h3>

                <ul className="mb-6 space-y-3 text-gray-700 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-yellow-400"></span>
                    Fresh strategy for thinkers & builders
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-pink-400"></span>
                    Hidden tool or framework
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-indigo-400"></span>
                    Rare opportunity or creative spark
                  </li>
                </ul>

                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs text-gray-700 font-medium flex items-center gap-2">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">
                      All in under 5 minutes
                    </span>
                  </p>
                </div>
              </div>

              {/* Secondary Newsletter Preview - Stacked for depth effect */}
              <div className="absolute top-8 -right-4 bg-white p-8 border border-gray-200 rounded-xl shadow-lg -rotate-2 z-0 opacity-70 w-80 h-80"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with Modern Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Every Friday, You&apos;ll Receive
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quality content that saves your time and expands your horizons
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                ),
                title: "Fresh Strategies",
                description:
                  "For thinkers, builders, and explorers seeking an edge",
              },
              {
                icon: (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                ),
                title: "Hidden Tools",
                description: "And frameworks you won't find trending elsewhere",
              },
              {
                icon: (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                ),
                title: "Curated Opportunities",
                description: "Across tech, business, and creativity spheres",
              },
              {
                icon: (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                ),
                title: "Short Reads",
                description: "That sharpen your mind — no doomscrolling",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="rounded-lg w-12 h-12 flex items-center justify-center bg-black text-white mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Issues Section */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Latest Issues
              </h2>
              <p className="text-lg text-gray-600">
                Catch up on what you might have missed
              </p>
            </div>
            <Link
              href="/previous-issues"
              className="inline-flex items-center text-sm font-medium text-black hover:text-gray-700"
            >
              View all
              <svg
                className="ml-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {latestIssues.map((issue) => (
              <Link
                key={issue.id}
                href={issue.link}
                className="group block bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold bg-black text-white px-3 py-1 rounded-full">
                      #{issue.id}
                    </span>
                    <span className="text-sm text-gray-500">{issue.date}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-black transition-colors">
                    {issue.title}
                  </h3>
                  <div className="flex justify-between items-center mt-6">
                    <span className="text-sm text-gray-500">5 min read</span>
                    <span className="text-black group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Our Readers Say
            </h2>
            <p className="text-lg text-gray-600">
              Join thousands of professionals who rely on WebFindsFriday to
              discover what matters
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {[
              {
                quote:
                  "WebFindsFriday consistently delivers the most valuable content of any newsletter in my inbox. It's the highlight of my week.",
                name: "Alex P.",
                title: "Product Manager",
                initial: "A",
              },
              {
                quote:
                  "I've discovered three game-changing tools and a business opportunity that fundamentally changed my work through this newsletter.",
                name: "Mira K.",
                title: "Entrepreneur",
                initial: "M",
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden"
              >
                <div className="md:flex">
                  <div className="p-8">
                    <div className="uppercase tracking-wide text-sm text-black font-semibold mb-1">
                      Testimonial
                    </div>
                    <div className="flex mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className="h-5 w-5 text-yellow-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <blockquote className="mt-2 text-xl leading-8 text-gray-800 mb-6">
                      &quot;{testimonial.quote}&quot;
                    </blockquote>
                    <div className="flex items-center mt-6">
                      <div className="flex-shrink-0 h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-xl font-bold text-gray-700">
                        {testimonial.initial}
                      </div>
                      <div className="ml-4">
                        <div className="text-lg font-medium text-gray-900">
                          {testimonial.name}
                        </div>
                        <div className="text-gray-600">{testimonial.title}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-yellow-400"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-indigo-400"></div>
        </div>

        <div className="relative max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to upgrade your weekly reading?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join 2,100+ curious minds who don&apos;t settle for average content
          </p>

          <div className="max-w-md mx-auto">
            <EmailSignup />
          </div>

          <p className="mt-6 text-sm text-gray-400">
            One email per week. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Modern Footer */}
      <footer className="bg-white py-12 border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex justify-center md:justify-start mb-8 md:mb-0">
              <Image
                src="/bannerLogo.png"
                alt="WebFindsFriday Logo"
                width={300}
                height={80}
                className="h-16 w-auto"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-y-4 gap-x-8">
              <Link
                href="/privacy"
                className="text-sm text-gray-600 hover:text-black"
              >
                Privacy
              </Link>
              <Link
                href="/terms"
                className="text-sm text-gray-600 hover:text-black"
              >
                Terms
              </Link>
              <Link
                href="/contact"
                className="text-sm text-gray-600 hover:text-black"
              >
                Contact
              </Link>
              <Link
                href="/previous-issues"
                className="text-sm text-gray-600 hover:text-black"
              >
                Archive
              </Link>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-100 pt-8 md:flex md:items-center md:justify-between text-sm text-gray-500">
            <div className="text-center md:text-left">
              © 2025 WebFindsFriday. All rights reserved.
            </div>

            <div className="mt-4 flex justify-center md:mt-0">
              <div className="flex space-x-6">
                {/* Social media icons removed for now */}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
